import {
  CheckItemsManager
} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/check-items-manager';
import {Step} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/step';
import {
  TestPassState,
  TestQuestion,
  TestResponse,
  TestResponseCommands
} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/models';
import {delay} from 'rxjs/operators';
import { Role} from '@app/modules/shared/data/constants';
import {Observable, of} from 'rxjs';


export class VideoCheckManager extends CheckItemsManager {
  icon = 'video-g';
  name = 'Video';

  constructor(userRole: Role) {
    super(userRole);
    this.steps.push(/*new ConnectingTheCamera(), */new TurningCameraAndMicOn(), new CheckingIfCameraIsWorking());
  }

  testPostProcessing() {
    if (this.steps[0].testResult.status === TestPassState.FAILED || this.steps[1].testResult.status === TestPassState.FAILED ) {
      this.steps[1].isHidden = true;
    }
  }
}

export class ConnectingTheCamera extends Step {
  name = 'Connecting the camera';
  title = 'Connecting the camera...';

  constructor() {
    super(true);
    this.testQuestion = new TestQuestion('Please make sure your camera is connected',
      [{text: 'I don\'t have one', value: 'no' ,color:'blue' ,isBlock:false,isDisabled:false},
        {text: 'It is plugged in or built in', value: 'yes' ,color:'blue' ,isBlock:false,isDisabled:false}],
    );
  }

  doRunStep() {
    return new Observable<TestResponse>(subscriber => {
      if (this.stepPayload === 'no') {
        this.testResult.status = TestPassState.FAILED;
        this.testResult.errorMassage = 'You will not be able to fully participate in class without a camera.' +
          ' Please plug in a USB webcam if you have one, then click retry. You may need to refresh this page and start diagnostics over again. ' +
          'If you do not have a plug-in camera, please get one, or use a computer with a built-in camera in the future. If you have done everything currently possible, ' +
          'skip this step.';
        this.testResult.text = ' no camera';
        this.testStatus='NO';
        subscriber.next(new TestResponse(TestResponseCommands.SHOW_ERROR));
      } else {
        this.testResult.status = TestPassState.PASSED;
        this.testResult.text = 'connected';
        this.testStatus='OK';
        subscriber.next(new TestResponse(TestResponseCommands.NEXT));
      }
    });
  }
}

export class TurningCameraAndMicOn extends Step {
  name = 'Turning camera and mic on';
  title = 'Turning camera and mic ON...';
  constructor() {
    super(false);
    this.testQuestion = new TestQuestion('Click \'Allow\' button on top of the page...',
      [{text: 'No', value: 'no' ,color:'blue' ,isBlock:false,isDisabled:false},
        {text: 'Yes, I can', value: 'yes' ,color:'blue' ,isBlock:false,isDisabled:false}],
    );
  }

  doRunStep() {
    return new Observable<TestResponse>(subscriber => {
      navigator.mediaDevices.getUserMedia({video: true})
        .then((stream: MediaStream) => {
          this.testResult.status = TestPassState.PASSED;
          this.testResult.text = 'allowed';
          this.testStatus = 'OK';
          subscriber.next(new TestResponse(TestResponseCommands.NEXT));
        })
        .catch((err: string) =>{
            this.testResult.status = TestPassState.FAILED;
          // eslint-disable-next-line max-len
            this.testResult.errorMassage = 'Please unblock your camera to participate in class. After unblocking, refresh the page to restart diagnostics. If you seek additional help, reach out to tech support using the chat bubble at the bottom or contact the office.';
            this.testResult.text = 'error';
            this.testStatus='NO';
            subscriber.next(new TestResponse(TestResponseCommands.VIDEO_MIC_IS_BLOCK));
        });
    });
  }
}

export class CheckingIfCameraIsWorking extends Step {
  name = 'Checking if camera is working';
  title = 'Checking if camera is working...';

  constructor() {
    super(true);
    this.testQuestion = new TestQuestion('Can you see yourself in the camera? Make sure that your camera is well-adjusted and that your face is seen clearly.',
      [{text: 'I cannot see myself :(', value: 'no' ,color:'blue' ,isBlock:false,isDisabled:false},
        {text: 'I look good', value: 'yes' ,color:'blue' ,isBlock:false,isDisabled:false}],
    );
  }

  doRunStep() {
    return new Observable<TestResponse>(subscriber => {
      if (this.stepPayload === 'no') {
        this.testResult.status = TestPassState.FAILED;
        // eslint-disable-next-line max-len
        this.testResult.errorMassage = 'Please try to restart or reconnect your camera, then click Retry. If you can\'t fix it now, click Skip. However, your teacher and the other students will not be able to see you and you will not be able to fully participate in class without a working camera. If you seek additional help, reach out to tech support using the chat bubble at the bottom or contact the office.';
        this.testStatus='NO';
        subscriber.next(new TestResponse(TestResponseCommands.SHOW_ERROR));
      } else {
        this.testResult.status = TestPassState.PASSED;
        this.testResult.text = 'working fine';
        this.testStatus='OK';
        subscriber.next(new TestResponse(TestResponseCommands.NEXT));
      }
    });
  }

  override runStep(): Observable<TestResponse> {
    if (!this._isPreparedForTest) {
      return of(new TestResponse(TestResponseCommands.REQUEST_VIDEO, this.testQuestion));
    } else {
      return this.doRunStep().pipe(delay(500));
    }
  };
}
